import { ApiProblem } from '@rythmo/api-client';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { inSchool, useApi } from '../api/api.js';
import { useAuth } from '../auth/AuthProvider.js';
import { NoAccess } from '../auth/pages.js';
import { useI18n } from '../i18n/I18n.js';
import { Notice } from '../ui/parts.js';
import { type BrandColours, brandColours } from './brand.js';
import { chooseSchool, type Me, openable, type School } from './choose.js';
import { Picker } from './Picker.js';

interface SchoolSession {
  me: Me;
  school: School;
  /** `null`: the school set no colour, or is not entitled to branding; Rythmo's own stays. */
  colours: BrandColours | null;
  logoUrl: string | null;
  /** More than one school can be opened, so switching means something. */
  canSwitch: boolean;
  switchSchool: () => void;
}

const SchoolContext = createContext<SchoolSession | null>(null);

const storageKey = (sub: string) => `rythmo.admin.school.${sub}`;

function remembered(sub: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(sub));
  } catch {
    return null;
  }
}

function remember(sub: string, tenantId: string | null): void {
  try {
    if (tenantId === null) window.localStorage.removeItem(storageKey(sub));
    else window.localStorage.setItem(storageKey(sub), tenantId);
  } catch {
    // A private window may refuse storage; the choice still holds for this tab.
  }
}

/**
 * Between sign-in and the console: asks `/v1/me` for the person's schools without naming one
 * (ADR-0085), then enters the one they may open, the one they chose last, or asks.
 */
export function SchoolGate({ children }: { children: ReactNode }) {
  const { hooks } = useApi();
  const { user, signIn } = useAuth();
  const { t } = useI18n();
  const sub = user?.profile.sub ?? '';
  const [chosen, setChosen] = useState<string | null>(() => remembered(sub));

  const me = hooks.gateway.useQuery('get', '/v1/me', undefined, {
    // A refusal is an answer, not a blip: retrying it changes nothing.
    retry: (count, error) => !(error instanceof ApiProblem && error.status < 500) && count < 1,
  });
  const status = me.error instanceof ApiProblem ? me.error.status : null;

  // The token was refused: sign in again, once, and come back here.
  const restarted = useRef(false);
  useEffect(() => {
    if (status === 401 && !restarted.current) {
      restarted.current = true;
      void signIn(`${window.location.pathname}${window.location.search}`);
    }
  }, [status, signIn]);

  if (me.isPending || status === 401) return <Starting />;
  // Signed in, but no membership at any school: the console is not for this account.
  if (status === 404 || status === 403) return <NoAccess />;
  if (me.isError) {
    return (
      <Notice
        title={t.picker.loadFailed}
        action={
          <button type="button" className="button button-primary" onClick={() => void me.refetch()}>
            {t.picker.tryAgain}
          </button>
        }
      />
    );
  }

  const choice = chooseSchool(me.data, chosen);
  if (choice.kind === 'none') return <NoAccess />;
  if (choice.kind === 'pick') {
    return (
      <Picker
        me={me.data}
        onPick={(tenantId) => {
          remember(sub, tenantId);
          setChosen(tenantId);
        }}
      />
    );
  }

  const school = me.data.schools.find((s) => s.tenantId === choice.tenantId);
  if (school === undefined) return <NoAccess />;

  return (
    <InSchool
      me={me.data}
      school={school}
      canSwitch={me.data.schools.filter(openable).length > 1}
      switchSchool={() => {
        remember(sub, null);
        setChosen(null);
      }}
    >
      {children}
    </InSchool>
  );
}

function InSchool({
  me,
  school,
  canSwitch,
  switchSchool,
  children,
}: Omit<SchoolSession, 'colours' | 'logoUrl'> & { children: ReactNode }) {
  const { hooks } = useApi();
  // Asked again inside the school: the first sight there accepts a pending membership (ADR-0031),
  // and the answer carries the roles held at that school (ADR-0085).
  const here = hooks.gateway.useQuery('get', '/v1/me', inSchool(school.tenantId));
  // Readable by anyone at the school; a 403 means the school is not entitled to branding, which
  // is a steady state: render Rythmo's defaults and say nothing (tenant-svc's FeaturesController).
  const branding = hooks.tenant.useQuery('get', '/v1/tenant/branding', inSchool(school.tenantId), {
    retry: false,
  });

  const value = useMemo<SchoolSession>(() => {
    const raw = branding.data?.branding ?? {};
    const logo = raw['logoUrl'];
    return {
      me,
      school:
        here.data?.tenantId === school.tenantId ? { ...school, roles: here.data.roles } : school,
      colours: brandColours(raw['primaryColor']),
      logoUrl: typeof logo === 'string' && logo.startsWith('https://') ? logo : null,
      canSwitch,
      switchSchool,
    };
  }, [me, school, here.data, branding.data, canSwitch, switchSchool]);

  return <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>;
}

function Starting() {
  const { t } = useI18n();
  return (
    <div className="starting" role="status">
      <span className="visually-hidden">{t.starting}</span>
    </div>
  );
}

export function useSchool(): SchoolSession {
  const value = useContext(SchoolContext);
  if (value === null) throw new Error('useSchool outside SchoolGate');
  return value;
}
